import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const LiveTransactionFeed = ({ transactions, maxItems = 10 }) => {
  const [displayedTransactions, setDisplayedTransactions] = useState([]);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!isPaused) {
      setDisplayedTransactions(transactions?.slice(0, maxItems));
    }
  }, [transactions, maxItems, isPaused]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: {
        bg: 'bg-success/10',
        text: 'text-success',
        icon: 'CheckCircle2',
        label: 'Berhasil'
      },
      pending: {
        bg: 'bg-warning/10',
        text: 'text-warning',
        icon: 'Clock',
        label: 'Pending'
      },
      failed: {
        bg: 'bg-error/10',
        text: 'text-error',
        icon: 'XCircle',
        label: 'Gagal'
      }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${config?.bg}`}>
        <Icon name={config?.icon} size={12} className={config?.text} />
        <span className={`text-xs font-medium ${config?.text}`}>{config?.label}</span>
      </div>
    );
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      'VA Transfer': 'Building2',
      'QRIS': 'QrCode',
      'OVO': 'Wallet',
      'DANA': 'Wallet',
      'GoPay': 'Wallet',
      'Credit Card': 'CreditCard',
      'Debit Card': 'CreditCard'
    };
    return icons?.[method] || 'DollarSign';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="card h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-1">
            Transaksi Live
          </h3>
          <p className="text-xs text-muted-foreground">
            Update real-time setiap detik
          </p>
        </div>
        <button
          onClick={() => setIsPaused(!isPaused)}
          className={`p-2 rounded-lg transition-smooth ${
            isPaused 
              ? 'bg-warning/10 text-warning hover:bg-warning/20' :'bg-success/10 text-success hover:bg-success/20'
          }`}
          title={isPaused ? 'Resume' : 'Pause'}
        >
          <Icon name={isPaused ? 'Play' : 'Pause'} size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto space-y-3 pr-2" style={{ maxHeight: '600px' }}>
        {displayedTransactions?.map((transaction, index) => (
          <div
            key={transaction?.id}
            className="p-3 bg-muted/50 rounded-lg hover:bg-muted transition-smooth border border-border"
            style={{
              animation: index === 0 && !isPaused ? 'slideDown 0.3s ease-out' : 'none'
            }}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon 
                    name={getPaymentMethodIcon(transaction?.paymentMethod)} 
                    size={16} 
                    className="text-primary"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {transaction?.merchantName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {transaction?.paymentMethod}
                  </p>
                </div>
              </div>
              {getStatusBadge(transaction?.status)}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">ID:</span>
                <span className="text-xs font-mono text-foreground">
                  {transaction?.transactionId}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {formatTime(transaction?.timestamp)}
              </span>
            </div>

            <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground data-text">
                Rp {transaction?.amount?.toLocaleString('id-ID')}
              </span>
              {transaction?.status === 'success' && (
                <span className="text-xs text-success flex items-center gap-1">
                  <Icon name="Zap" size={12} />
                  {transaction?.processingTime}ms
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Menampilkan {displayedTransactions?.length} transaksi terbaru</span>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span>Live</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTransactionFeed;